import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BusinessHours } from "@/types/store";
import { formatTime } from "@/utils/businessHours";

interface BusinessHoursEditorProps {
  businessHours: Record<string, BusinessHours>;
  onChange: (day: string, hours: Partial<BusinessHours>) => void;
  disabled?: boolean;
}

const DAYS = [
  { key: "monday", label: "Monday" },
  { key: "tuesday", label: "Tuesday" },
  { key: "wednesday", label: "Wednesday" },
  { key: "thursday", label: "Thursday" },
  { key: "friday", label: "Friday" },
  { key: "saturday", label: "Saturday" },
  { key: "sunday", label: "Sunday" },
];

const HOURS = Array.from({ length: 24 }, (_, i) => {
  const hour = i.toString().padStart(2, "0");
  return { value: `${hour}:00`, label: `${hour}:00` };
});

export function BusinessHoursEditor({
  businessHours,
  onChange,
  disabled = false,
}: BusinessHoursEditorProps) {
  // Ensure we have complete business hours for all days
  const defaultDayHours = { open: "09:00", close: "17:00", closed: false };
  
  // Initialize any missing days with default values
  DAYS.forEach(({ key }) => {
    if (!businessHours[key]) {
      console.log(`Day ${key} missing from business hours, adding default values`);
      onChange(key, defaultDayHours);
    }
  });

  return (
    <div className="space-y-4">
      {DAYS.map(({ key, label }) => (
        <div key={key} className="flex items-center gap-4 p-2 rounded-lg border">
          <div className="w-32">
            <Label>{label}</Label>
          </div>
          
          <div className="flex items-center gap-2">
            <Switch
              checked={!businessHours[key]?.closed}
              onCheckedChange={(checked) => {
                const newValue = !checked;
                console.log(`Setting ${key} to ${checked ? 'open' : 'closed'}`);
                console.log("Current state:", businessHours[key]);
                console.log("New state:", { ...businessHours[key], closed: newValue });
                onChange(key, { closed: newValue });
              }}
              disabled={disabled}
            />
            <span className="text-sm text-gray-500">
              {businessHours[key]?.closed ? "Closed" : "Open"}
            </span>
          </div>

          {!businessHours[key]?.closed && (
            <>
              <div className="flex items-center gap-2">
                <Select
                  value={businessHours[key]?.open}
                  onValueChange={(value) => {
                    console.log(`Setting ${key} opening time to ${value}`);
                    console.log("Current state:", businessHours[key]);
                    console.log("New state:", { ...businessHours[key], open: value });
                    onChange(key, { open: value });
                  }}
                  disabled={disabled}
                >
                  <SelectTrigger className="w-[120px]">
                    <SelectValue placeholder="Opening time" />
                  </SelectTrigger>
                  <SelectContent>
                    {HOURS.map(({ value, label }) => (
                      <SelectItem key={value} value={value}>
                        {label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <span>to</span>
                <Select
                  value={businessHours[key]?.close}
                  onValueChange={(value) => {
                    console.log(`Setting ${key} closing time to ${value}`);
                    console.log("Current state:", businessHours[key]);
                    console.log("New state:", { ...businessHours[key], close: value });
                    onChange(key, { close: value });
                  }}
                  disabled={disabled}
                >
                  <SelectTrigger className="w-[120px]">
                    <SelectValue placeholder="Closing time" />
                  </SelectTrigger>
                  <SelectContent>
                    {HOURS.map(({ value, label }) => (
                      <SelectItem key={value} value={value}>
                        {label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </>
          )}
        </div>
      ))}
    </div>
  );
} 